(() => {
  const allowedEvents = new Set([
    'view_home',
    'switch_language',
    'click_apply',
    'start_application',
    'application_validation_error',
    'submit_application',
    'application_success',
    'expand_faq',
  ]);
  const sent = new Set();
  const pending = new Set();
  const submitSessionKey = 'flexora-application-submit-session';
  const submitSessionTtl = 10 * 60 * 1000;
  let cleanupTasks = [];
  let reinitFrame;

  const safelyDispatch = (eventKey, once) => {
    const dispatch = window.Shopify?.analytics?.publish;
    if (typeof dispatch !== 'function') {
      pending.delete(eventKey);
      return;
    }

    try {
      const outcome = dispatch(eventKey, {});
      if (once) sent.add(eventKey);
      pending.delete(eventKey);
      if (outcome && typeof outcome.then === 'function') {
        Promise.resolve(outcome).catch(() => {
          if (once) sent.delete(eventKey);
        });
      }
    } catch (_error) {
      pending.delete(eventKey);
    }
  };

  const publish = (eventKey, once = false) => {
    if (!allowedEvents.has(eventKey) || (once && (sent.has(eventKey) || pending.has(eventKey)))) return;
    const isAllowed = window.Shopify?.customerPrivacy?.analyticsProcessingAllowed;
    if (typeof isAllowed !== 'function') return;
    if (once) pending.add(eventKey);

    try {
      const consent = isAllowed();
      if (consent && typeof consent.then === 'function') {
        Promise.resolve(consent)
          .then((allowed) => {
            if (allowed) safelyDispatch(eventKey, once);
            else pending.delete(eventKey);
          })
          .catch(() => pending.delete(eventKey));
        return;
      }
      if (consent) safelyDispatch(eventKey, once);
      else pending.delete(eventKey);
    } catch (_error) {
      pending.delete(eventKey);
    }
  };

  const listen = (target, type, handler, options) => {
    target.addEventListener(type, handler, options);
    cleanupTasks.push(() => target.removeEventListener(type, handler, options));
  };

  function cleanup() {
    cleanupTasks.splice(0).forEach((task) => task());
  }

  const consumeFreshSubmitSession = () => {
    try {
      const stamp = Number(window.sessionStorage.getItem(submitSessionKey));
      window.sessionStorage.removeItem(submitSessionKey);
      return Number.isFinite(stamp) && stamp > 0 && Date.now() - stamp <= submitSessionTtl && Date.now() >= stamp;
    } catch (_error) {
      return false;
    }
  };

  function init() {
    if (reinitFrame !== undefined) {
      window.cancelAnimationFrame(reinitFrame);
      reinitFrame = undefined;
    }
    cleanup();

    if (document.body.dataset.pageType === 'index') publish('view_home', true);

    document.querySelectorAll('[data-analytics-apply]').forEach((link) => {
      listen(link, 'click', () => publish('click_apply'));
    });

    document.querySelectorAll('[data-analytics-locale]').forEach((option) => {
      listen(option, 'change', () => publish('switch_language'));
    });

    document.querySelectorAll('[data-faq]').forEach((faq) => {
      listen(faq, 'toggle', () => {
        if (faq.open) publish('expand_faq');
      });
    });

    document.querySelectorAll('[data-application-form]').forEach((application) => {
      listen(application, 'focusin', () => publish('start_application', true));
      listen(application, 'invalid', () => publish('application_validation_error', true), true);
      listen(application, 'submit', (event) => {
        if (event.defaultPrevented) {
          if (!application.classList.contains('is-submitting')) {
            publish('application_validation_error', true);
          }
          return;
        }
        publish('submit_application');
      });
    });

    if (document.body.dataset.pageHandle === 'thank-you' && consumeFreshSubmitSession()) {
      publish('application_success', true);
    }
  }

  const handleSectionLoad = () => init();
  const handleSectionUnload = () => {
    cleanup();
    if (reinitFrame !== undefined) window.cancelAnimationFrame(reinitFrame);
    reinitFrame = window.requestAnimationFrame(() => {
      reinitFrame = undefined;
      init();
    });
  };
  const handlePageHide = () => {
    if (reinitFrame !== undefined) window.cancelAnimationFrame(reinitFrame);
    reinitFrame = undefined;
    cleanup();
  };

  document.addEventListener('shopify:section:load', handleSectionLoad);
  document.addEventListener('shopify:section:unload', handleSectionUnload);
  window.addEventListener('pagehide', handlePageHide, { once: true });
  init();
})();
